import { Heart } from 'lucide-react';
import {
  Users, Bed, Bath, Wifi, Car, Snowflake, Tv,
  MapPin, ShieldCheck, Clock, Key, DoorOpen, DoorClosed,
  ChevronDown, ChevronUp,
  Coffee, Utensils, WashingMachine, Wind, Flame, Dog,
  Mountain, Waves, TreePine, Sun, Droplets, Lock, Shirt,
  Monitor, Armchair, Heater, Fan, BedDouble, Refrigerator,
  Dumbbell, Baby, Cigarette, CigaretteOff, Umbrella,
  Sparkles, ShowerHead, Thermometer, Speaker, Camera,
  BookOpen, Gamepad2, Bike, Anchor, Fish, Tent,
  CircleParking, Eye, Plug, Blinds,
} from 'lucide-react';
import { useState } from 'react';

const CANCELLATION_STYLES = {
  flexible: 'bg-[#ECFCCB] border border-[#3F6212]/20',
  moderate: 'bg-[#FEF9C3] border border-[#A16207]/20',
  strict: 'bg-[#FEE2E2] border border-[#DC2626]/20',
};

const COUNTRY_FLAGS = { Argentina: '🇦🇷', Brasil: '🇧🇷', Paraguay: '🇵🇾' };

// Comprehensive amenity → icon mapping (covers most Airbnb amenities)
const AMENITY_ICON_MAP = {
  // Connectivity & Entertainment
  'wifi': Wifi, 'internet': Wifi, 'tv': Tv, 'televisión': Tv, 'television': Tv,
  'cable': Tv, 'netflix': Monitor, 'parlante': Speaker, 'bluetooth': Speaker,
  'juego': Gamepad2, 'libros': BookOpen, 'lecturas': BookOpen,
  // Kitchen & Cooking
  'cocina': Utensils, 'utensilios': Utensils, 'microondas': Utensils,
  'horno': Flame, 'heladera': Refrigerator, 'refrigerador': Refrigerator,
  'congelador': Refrigerator, 'cafetera': Coffee, 'café': Coffee,
  'pava': Coffee, 'tostador': Utensils, 'mesa de comedor': Utensils,
  // Laundry & Clothing
  'lavarropas': WashingMachine, 'lavadora': WashingMachine, 'secarropas': Wind,
  'secadora': Wind, 'plancha': Sparkles, 'tendedero': Shirt, 'ropa de cama': Bed,
  'guardar ropa': Shirt, 'percha': Shirt, 'armario': Shirt,
  // Bathroom
  'agua caliente': Droplets, 'shampoo': Droplets, 'jabón': Droplets,
  'ducha': ShowerHead, 'bañera': Bath, 'baño privado': Bath, 'bidet': Droplets,
  'secador de pelo': Wind, 'toallas': Bath, 'productos de limpieza': Sparkles,
  // Climate
  'aire acondicionado': Snowflake, 'ac:': Snowflake, 'split': Snowflake,
  'calefacción': Heater, 'calefactor': Heater, 'estufa': Heater,
  'ventilador': Fan, 'chimenea': Flame,
  // Outdoor & Nature
  'piscina': Waves, 'pileta': Waves, 'jacuzzi': Waves, 'playa': Umbrella,
  'parrilla': Flame, 'asador': Flame, 'barbacoa': Flame,
  'balcón': Sun, 'terraza': Sun, 'patio': TreePine, 'jardín': TreePine,
  'vista': Eye, 'montaña': Mountain, 'lago': Fish, 'río': Anchor,
  'al aire libre': Tent, 'hamaca': Tent,
  // Parking & Transport
  'estacionamiento': Car, 'garaje': Car, 'cochera': Car, 'parking': CircleParking,
  'bicicleta': Bike,
  // Safety & Security
  'caja fuerte': Lock, 'cerradura': Lock, 'alarma': ShieldCheck,
  'detector de humo': ShieldCheck, 'matafuego': ShieldCheck, 'extintor': ShieldCheck,
  'cámara': Camera, 'botiquín': ShieldCheck, 'primeros auxilios': ShieldCheck,
  // Family & Accessibility
  'cuna': Baby, 'bebé': Baby, 'infantil': Baby, 'niño': Baby,
  'mascota': Dog, 'perro': Dog,
  'gimnasio': Dumbbell, 'ejercicio': Dumbbell, 'zona de trabajo': Monitor,
  'escritorio': Monitor, 'trabajo': Monitor,
  // Basics
  'básico': Sparkles, 'almohada': Bed, 'manta': Bed, 'sábana': Bed,
  'cortina': Blinds, 'persiana': Blinds, 'oscura': Blinds,
  'entrada independiente': DoorOpen, 'acceso': Key,
  'enchufe': Plug, 'electricidad': Plug,
  'lavandería': WashingMachine, 'limpieza': Sparkles,
  // Smoking
  'fumar': CigaretteOff, 'no fumar': CigaretteOff,
  // Temperature
  'caliente': Thermometer, 'termostato': Thermometer,
};

function getAmenityIcon(name) {
  const lower = name.toLowerCase();
  for (const [key, Icon] of Object.entries(AMENITY_ICON_MAP)) {
    if (lower.includes(key)) return Icon;
  }
  return null;
}

function PropertyCapacity({ property }) {
  return (
    <div className="flex flex-wrap gap-6 pb-6 border-b border-[#E7E5E4]">
      <div className="flex items-center">
        <Users className="w-5 h-5 mr-2 text-[#264639]" />
        <span className="text-[#1C1917] font-medium">{property.guests} huéspedes</span>
      </div>
      {property.bedrooms && (
        <div className="flex items-center">
          <Bed className="w-5 h-5 mr-2 text-[#264639]" />
          <span className="text-[#1C1917] font-medium">{property.bedrooms} dormitorios</span>
        </div>
      )}
      {property.beds && (
        <div className="flex items-center">
          <Bed className="w-5 h-5 mr-2 text-[#264639]" />
          <span className="text-[#1C1917] font-medium">{property.beds} camas</span>
        </div>
      )}
      {property.bathrooms && (
        <div className="flex items-center">
          <Bath className="w-5 h-5 mr-2 text-[#264639]" />
          <span className="text-[#1C1917] font-medium">{property.bathrooms} baños</span>
        </div>
      )}
    </div>
  );
}

function CheckinInfoSection({ property, isAuthenticated, t }) {
  if (!property.check_in_time && !property.check_out_time && !property.checkin_instructions && !property.checkout_instructions && !property.access_code) {
    return null;
  }
  return (
    <div className="pt-6 border-t border-[#E7E5E4]" data-testid="checkin-info-section">
      <h3 className="text-lg font-semibold text-[#1C1917] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
        <Clock className="w-5 h-5 text-[#264639]" /> {t('detail.checkin')}
      </h3>
      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        {property.check_in_time && (
          <div className="flex items-center gap-3 p-3 bg-[#DCE9E2] rounded-lg">
            <DoorOpen className="w-5 h-5 text-[#264639]" />
            <div>
              <p className="text-xs text-[#78716C]">{t('detail.checkinTime')}</p>
              <p className="font-semibold text-[#1C1917]">{t('detail.checkinFrom')} {property.check_in_time}</p>
            </div>
          </div>
        )}
        {property.check_out_time && (
          <div className="flex items-center gap-3 p-3 bg-[#F5E4DE] rounded-lg">
            <DoorClosed className="w-5 h-5 text-[#C36F4D]" />
            <div>
              <p className="text-xs text-[#78716C]">{t('detail.checkoutTime')}</p>
              <p className="font-semibold text-[#1C1917]">{t('detail.checkoutBefore')} {property.check_out_time}</p>
            </div>
          </div>
        )}
      </div>
      {isAuthenticated && property.access_code && (
        <div className="flex items-center gap-3 p-3 bg-[#F5F4F0] rounded-lg mb-3" data-testid="access-code-display">
          <Key className="w-5 h-5 text-[#264639]" />
          <div className="flex-1">
            <p className="text-xs text-[#78716C]">{t('detail.accessCode')}</p>
            <p className="font-mono font-semibold text-[#1C1917]">{property.access_code}</p>
          </div>
        </div>
      )}
      {property.checkin_instructions && (
        <div className="p-4 bg-[#F5F4F0] rounded-lg mb-3" data-testid="checkin-instructions-display">
          <p className="text-sm font-medium text-[#264639] mb-1">{t('detail.checkinInstructions')}</p>
          <p className="text-sm text-[#57534E] whitespace-pre-line">{property.checkin_instructions}</p>
        </div>
      )}
      {property.checkout_instructions && (
        <div className="p-4 bg-[#F5F4F0] rounded-lg" data-testid="checkout-instructions-display">
          <p className="text-sm font-medium text-[#C36F4D] mb-1">{t('detail.checkoutInstructions')}</p>
          <p className="text-sm text-[#57534E] whitespace-pre-line">{property.checkout_instructions}</p>
        </div>
      )}
    </div>
  );
}

function CancellationPolicySection({ property, t }) {
  if (!property.cancellation_policy) return null;
  return (
    <div className="pt-6 border-t border-[#E7E5E4]" data-testid="cancellation-policy-section">
      <h3 className="text-lg font-semibold text-[#1C1917] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
        <ShieldCheck className="w-5 h-5 text-[#264639]" /> {t('detail.cancellation')}
      </h3>
      <div className={`rounded-lg p-4 ${CANCELLATION_STYLES[property.cancellation_policy] || 'bg-[#F5F4F0] border border-[#E7E5E4]'}`}>
        <p className="font-semibold text-[#1C1917] capitalize mb-1">
          {t(`detail.cancellation.${property.cancellation_policy}`)}
        </p>
        <p className="text-sm text-[#57534E]">
          {t(`detail.cancellation.${property.cancellation_policy}.desc`)}
        </p>
      </div>
    </div>
  );
}

function GuestPoliciesSection({ property, t }) {
  if (property.pets_allowed === undefined && property.infants_allowed === undefined
    && property.children_allowed === undefined && property.smoking_allowed === undefined
    && !property.minimum_nights) {
    return null;
  }
  const policies = [
    { key: 'pets_allowed', label: t('detail.petsAllowed') || 'Mascotas' },
    { key: 'children_allowed', label: t('detail.childrenAllowed') || 'Niños' },
    { key: 'infants_allowed', label: t('detail.infantsAllowed') || 'Bebés' },
    { key: 'smoking_allowed', label: t('detail.smokingAllowed') || 'Fumar' },
  ];
  return (
    <div className="pt-6 border-t border-[#E7E5E4]" data-testid="guest-policies-section">
      <h3 className="text-lg font-semibold text-[#1C1917] mb-4" style={{ fontFamily: 'Outfit' }}>
        {t('detail.policies') || 'Políticas'}
      </h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {policies.map(({ key, label }) =>
          property[key] !== undefined ? (
            <div key={key} className={`flex items-center gap-2 p-3 rounded-lg text-sm ${property[key] ? 'bg-[#ECFCCB] text-[#3F6212]' : 'bg-[#FEE2E2] text-[#DC2626]'}`}>
              {property[key] ? '✓' : '✕'} {label}
            </div>
          ) : null
        )}
        {property.minimum_nights && (
          <div className="flex items-center gap-2 p-3 rounded-lg text-sm bg-[#F5F4F0] text-[#57534E]">
            {t('detail.minNights') || 'Min'}: {property.minimum_nights} {t('detail.nights') || 'noches'}
          </div>
        )}
        {property.maximum_nights && (
          <div className="flex items-center gap-2 p-3 rounded-lg text-sm bg-[#F5F4F0] text-[#57534E]">
            {t('detail.maxNights') || 'Max'}: {property.maximum_nights} {t('detail.nights') || 'noches'}
          </div>
        )}
      </div>
    </div>
  );
}


function AmenitiesSection({ property }) {
  const [showAll, setShowAll] = useState(false);
  const amenities = property.amenities || [];

  if (amenities.length === 0) return null;

  const displayed = showAll ? amenities : amenities.slice(0, 12);

  return (
    <div className="py-6 border-b border-[#E7E5E4]">
      <h3 className="text-lg font-semibold text-[#1C1917] mb-4" style={{ fontFamily: 'Outfit' }}>
        Servicios ({amenities.length})
      </h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {displayed.map((name) => {
          const Icon = getAmenityIcon(name);
          return (
            <div key={name} className="flex items-center text-[#57534E] text-sm py-1.5">
              {Icon ? <Icon className="w-4 h-4 mr-2 text-[#264639] flex-shrink-0" /> : <span className="w-4 h-4 mr-2 rounded-full bg-[#264639]/10 flex-shrink-0" />}
              {name}
            </div>
          );
        })}
      </div>
      {amenities.length > 12 && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="mt-3 text-sm font-medium text-[#264639] hover:underline flex items-center gap-1"
          data-testid="show-all-amenities-btn"
        >
          {showAll ? <><ChevronUp className="w-4 h-4" /> Mostrar menos</> : <><ChevronDown className="w-4 h-4" /> Mostrar los {amenities.length} servicios</>}
        </button>
      )}
    </div>
  );
}

function DescriptionSection({ property, t }) {
  const [expanded, setExpanded] = useState(false);
  const desc = property.description || property.airbnb_summary || property.internal_name || '';

  if (!desc) return null;

  const isLong = desc.length > 400;
  const displayText = isLong && !expanded ? desc.slice(0, 400) + '...' : desc;

  return (
    <div className="pt-6">
      <h3 className="text-lg font-semibold text-[#1C1917] mb-4" style={{ fontFamily: 'Outfit' }}>{t('detail.description')}</h3>
      <p className="text-[#57534E] leading-relaxed whitespace-pre-line">{displayText}</p>
      {isLong && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-2 text-sm font-medium text-[#264639] hover:underline flex items-center gap-1"
          data-testid="description-toggle-btn"
        >
          {expanded ? <><ChevronUp className="w-4 h-4" /> Mostrar menos</> : <><ChevronDown className="w-4 h-4" /> Leer más</>}
        </button>
      )}
    </div>
  );
}

export function PropertyInfoSection({ property, isAuthenticated, isFavorite, favCount, onToggleFavorite, t }) {
  return (
    <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="property-info-section">
      <div className="flex items-center gap-2 mb-4">
        <span className="bg-[#DCE9E2] text-[#264639] px-3 py-1 rounded-full text-sm font-medium">
          {property.property_type || 'Hotel'}
        </span>
        {property.instant_booking && (
          <span className="bg-[#F5E4DE] text-[#C36F4D] px-3 py-1 rounded-full text-sm font-medium">Reserva Inmediata</span>
        )}
        {property.code && <span className="text-[#78716C] text-sm">Código: {property.code}</span>}
      </div>

      <div className="flex items-start justify-between gap-4 mb-4">
        <h1 className="text-2xl sm:text-3xl font-bold text-[#1C1917]" style={{ fontFamily: 'Outfit' }} data-testid="property-title">
          {property.title}
        </h1>
        <button onClick={onToggleFavorite} className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#E7E5E4] hover:border-[#C36F4D] transition-all hover:scale-105 flex-shrink-0" data-testid="detail-fav-btn">
          <Heart className={`w-5 h-5 transition-colors ${isFavorite ? 'text-[#C36F4D] fill-[#C36F4D]' : 'text-[#57534E]'}`} />
          {favCount > 0 && <span className="text-xs text-[#78716C]">{favCount}</span>}
        </button>
      </div>

      {property.property_name && <p className="text-sm text-[#C36F4D] font-medium mb-1">{property.property_name}</p>}
      <div className="flex items-center text-[#57534E] mb-6">
        <MapPin className="w-5 h-5 mr-2 text-[#C36F4D]" />
        <span>{property.city || property.location}</span>
        {property.state && <span>, {property.state}</span>}
        {property.country && (
          <span className="ml-2">{COUNTRY_FLAGS[property.country] || '🏳️'} {property.country}</span>
        )}
      </div>

      <PropertyCapacity property={property} />

      {/* Amenities / Servicios */}
      <AmenitiesSection property={property} />

      {/* Description */}
      <DescriptionSection property={property} t={t} />

      <CancellationPolicySection property={property} t={t} />
      <CheckinInfoSection property={property} isAuthenticated={isAuthenticated} t={t} />

      {/* House Rules */}
      {property.house_rules && (
        <div className="pt-6 border-t border-[#E7E5E4]">
          <h3 className="text-lg font-semibold text-[#1C1917] mb-4" style={{ fontFamily: 'Outfit' }}>{t('detail.houseRules')}</h3>
          <p className="text-[#57534E] leading-relaxed whitespace-pre-line">{property.house_rules}</p>
        </div>
      )}

      <GuestPoliciesSection property={property} t={t} />
    </div>
  );
}
